import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  FAMILY_PAYMENTS_DISCLAIMER,
  debtRiskComponentBullets,
  debtRiskHeadline,
  eligibilityGapHeadline,
  eligibilityGapSupplementBullet,
  entitlementHeadline,
  partABullet,
  partBBullet,
  sourceBullet,
  supplementBullet,
} from "@aucash/rules/family-payments";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import {
  Checkbox,
  Field,
  MoneyInput,
  Segmented,
  Stepper,
} from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// family-payments — the Family Tax Benefit (FTB) + Child Care Subsidy
// reconciliation calculator, surfaced stateless-v1 (PLAN arch decision #1). One
// screen, three surfaces over the shared Services Australia ruleset; the result
// renders INLINE (no save-by-id, no auth gate, no persistence). Each compute is
// a stateless `calc.familyPayments.compute` query — input -> output, no
// round-trip to a stored row. Copy comes verbatim from the rules layer's copy
// helpers so the wording can never drift from the API/SEO surfaces.
//
// REGULATORY (validation §3, binding): FTB is a Services Australia payment, NOT
// a financial product — info-not-advice, $0 affiliate, NO product/credit
// handoff on this personalised surface. Every result is a "personalised
// summary", never a Services Australia entitlement determination (that caveat is
// rendered). The debt-risk surface is calm + protective ("at least this much,
// possibly more"), never "you'll owe $X" / "you should". The youngest-child age
// drives the Part B band; the app derives it from the children entered.

type Mode = "entitlement" | "eligibility-gap" | "debt-risk";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "entitlement", label: "FTB estimate" },
  { value: "eligibility-gap", label: "Am I eligible?" },
  { value: "debt-risk", label: "Reconciliation" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  entitlement: {
    eyebrow: "FY26 · Family Tax Benefit",
    title: "Estimate your Family Tax Benefit",
    blurb:
      "See an estimate of FTB Part A and Part B for the year, plus the end-of-year supplements, from your family income and children. The figures use current Services Australia rates — your actual entitlement is determined by Services Australia.",
  },
  "eligibility-gap": {
    eyebrow: "FY26 · eligibility",
    title: "Could you still be eligible for FTB?",
    blurb:
      "FTB Part A's base rate keeps paying well above the maximum-rate cutoff many families assume. This is a quick check of where your family income sits — it's easy to confirm your eligibility with Services Australia.",
  },
  "debt-risk": {
    eyebrow: "FY26 · reconciliation",
    title: "How a low income estimate can build a debt",
    blurb:
      "One family income estimate drives both FTB and Child Care Subsidy. If your actual income comes in higher, both can be reconciled at balancing. Child Care Subsidy withholds a buffer; FTB does not. See the indicative exposure, shown separately.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.familyPayments.compute.query>
>;

/** A child row in the form — just an age (the rules engine bands it). */
interface ChildRow {
  age: number;
}

export default function FamilyPaymentsIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("entitlement");

  // entitlement + eligibility-gap
  const [familyAti, setFamilyAti] = useState("");
  const [children, setChildren] = useState<ChildRow[]>([{ age: 3 }]);
  const [claimPartB, setClaimPartB] = useState(true);
  const [singleParent, setSingleParent] = useState(false);
  const [primaryEarnerAti, setPrimaryEarnerAti] = useState("");
  const [secondaryEarnerAti, setSecondaryEarnerAti] = useState("");

  // debt-risk
  const [estimatedFamilyAti, setEstimatedFamilyAti] = useState("");
  const [projectedActualAti, setProjectedActualAti] = useState("");
  const [ftbPaidPerFortnight, setFtbPaidPerFortnight] = useState("");
  const [ccsPaidPerFortnight, setCcsPaidPerFortnight] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const childCount = children.length;

  const setChildCount = (next: number): void => {
    setChildren((prev) => {
      if (next > prev.length) {
        const added = Array.from({ length: next - prev.length }, () => ({
          age: 3,
        }));
        return [...prev, ...added];
      }
      return prev.slice(0, next);
    });
  };

  const setChildAge = (index: number, age: number): void => {
    setChildren((prev) => prev.map((c, i) => (i === index ? { age } : c)));
  };

  /** Youngest child's age — drives the Part B rate band (the app supplies it). */
  const youngestChildAge =
    children.length > 0
      ? children.reduce((min, c) => Math.min(min, c.age), children[0]!.age)
      : 0;

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.familyPayments.compute.query>[0]["input"];
  } | null => {
    switch (mode) {
      case "entitlement":
        return {
          input: {
            mode: "entitlement",
            familyAti: num(familyAti),
            children: children.map((c) => ({ age: c.age })),
            claimPartB,
            youngestChildAge: Math.min(18, youngestChildAge),
            primaryEarnerAti: num(primaryEarnerAti),
            secondaryEarnerAti: num(secondaryEarnerAti),
            singleParent,
          },
        };
      case "eligibility-gap":
        return {
          input: {
            mode: "eligibility-gap",
            familyAti: num(familyAti),
            childCount: Math.max(1, childCount),
          },
        };
      case "debt-risk":
        return {
          input: {
            mode: "debt-risk",
            estimatedFamilyAti: num(estimatedFamilyAti),
            projectedActualAti: num(projectedActualAti),
            ftbPaidPerFortnight: num(ftbPaidPerFortnight),
            ccsPaidPerFortnight: num(ccsPaidPerFortnight),
          },
        };
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "debt-risk") {
      if (estimatedFamilyAti.trim() === "")
        next.estimate = "Enter the family income estimate you lodged.";
      if (projectedActualAti.trim() === "")
        next.projected = "Enter your likely actual family income.";
      return next;
    }
    if (familyAti.trim() === "")
      next.familyAti = "Enter your combined family income for the year.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.familyPayments.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Family payments" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "debt-risk" ? (
            <>
              <Field
                label="Family income estimate you lodged"
                help="The combined family income figure you gave Services Australia — the one that drives both FTB and Child Care Subsidy."
                error={errors.estimate}
              >
                <MoneyInput
                  value={estimatedFamilyAti}
                  onChange={setEstimatedFamilyAti}
                  testID="input-estimated-ati"
                  accessibilityLabel="Family income estimate you lodged"
                />
              </Field>
              <Field
                label="Your likely actual family income"
                help="A better estimate of where your combined family income will actually land for the year."
                error={errors.projected}
              >
                <MoneyInput
                  value={projectedActualAti}
                  onChange={setProjectedActualAti}
                  testID="input-projected-ati"
                  accessibilityLabel="Your likely actual family income"
                />
              </Field>
              <Field
                label="FTB paid per fortnight (if known)"
                help="The Family Tax Benefit you currently receive each fortnight, from your Services Australia statement."
              >
                <MoneyInput
                  value={ftbPaidPerFortnight}
                  onChange={setFtbPaidPerFortnight}
                  accessibilityLabel="FTB paid per fortnight"
                />
              </Field>
              <Field
                label="Child Care Subsidy paid per fortnight (if known)"
                help="The Child Care Subsidy you currently receive each fortnight."
              >
                <MoneyInput
                  value={ccsPaidPerFortnight}
                  onChange={setCcsPaidPerFortnight}
                  accessibilityLabel="Child Care Subsidy paid per fortnight"
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Your combined family income for FY26"
                help="Your family's adjusted taxable income — broadly, both partners' taxable income for the year."
                error={errors.familyAti}
              >
                <MoneyInput
                  value={familyAti}
                  onChange={setFamilyAti}
                  testID="input-family-ati"
                  accessibilityLabel="Your combined family income for FY26"
                />
              </Field>

              <Field
                label="How many dependent children?"
                help="Children you claim Family Tax Benefit for."
              >
                <Stepper
                  value={childCount}
                  onChange={setChildCount}
                  min={mode === "eligibility-gap" ? 1 : 0}
                  max={8}
                  suffix={childCount === 1 ? "child" : "children"}
                  testID="children-count"
                  accessibilityLabel="Number of dependent children"
                />
              </Field>

              {mode === "entitlement" ? (
                <>
                  {children.map((child, i) => (
                    <Field
                      key={i}
                      label={`Age of child ${i + 1}`}
                      help={
                        i === 0
                          ? "The child's age sets which FTB Part A rate band applies (0–12 vs 13–19)."
                          : undefined
                      }
                    >
                      <Stepper
                        value={child.age}
                        onChange={(age) => setChildAge(i, age)}
                        min={0}
                        max={19}
                        suffix={child.age === 1 ? "year" : "years"}
                        testID={`child-age-${i}`}
                        accessibilityLabel={`Age of child ${i + 1}`}
                      />
                    </Field>
                  ))}

                  <Field label="Are you a single-parent family?">
                    <Checkbox
                      checked={singleParent}
                      onChange={setSingleParent}
                      label="Yes — there's one parent / carer (no secondary-earner income test for Part B)."
                    />
                  </Field>

                  <Field label="Include FTB Part B in this estimate?">
                    <Checkbox
                      checked={claimPartB}
                      onChange={setClaimPartB}
                      label="Yes — include Part B (single parent, sole or main earner, or carer of a child under the Part B age limit)."
                    />
                  </Field>

                  {claimPartB ? (
                    <>
                      <Field
                        label="Higher earner's income (primary earner)"
                        help="The higher individual income in the couple. For a single parent, this is your income. Above the Part B limit, Part B does not apply."
                      >
                        <MoneyInput
                          value={primaryEarnerAti}
                          onChange={setPrimaryEarnerAti}
                          accessibilityLabel="Higher earner's income"
                        />
                      </Field>
                      {singleParent ? null : (
                        <Field
                          label="Lower earner's income (secondary earner)"
                          help="The lower individual income in the couple. Part B reduces as this rises above the secondary-earner free area."
                        >
                          <MoneyInput
                            value={secondaryEarnerAti}
                            onChange={setSecondaryEarnerAti}
                            accessibilityLabel="Lower earner's income"
                          />
                        </Field>
                      )}
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          )}

          <Button
            label={submitting ? "Calculating…" : "See your summary →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="family-payments-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer FAMILY_PAYMENTS_DISCLAIMER) sits at the TOP,
 * before any number (validation §3.2 placement rule), and explicitly frames the
 * output as a personalised summary, NOT a Services Australia determination. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullets } = summaryFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. Names Services
          Australia as the authoritative finaliser. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FAMILY_PAYMENTS_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your personalised summary · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      {/* The personalised-summary caveat — never a Services Australia
          entitlement determination. */}
      <Text style={styles.caveat}>
        This is a personalised summary based on the figures you entered. It is not
        a Services Australia entitlement determination — your actual payment is
        worked out and finalised by Services Australia at balancing.
      </Text>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.periodLabel, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helpers for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing. */
function summaryFor(result: ComputeResult): {
  headline: string;
  bullets: string[];
} {
  const out = result.output;
  switch (result.mode) {
    case "entitlement": {
      const o = out as Extract<typeof out, { estimatedAnnualTotal: number }>;
      return {
        headline: entitlementHeadline(o),
        bullets: [partABullet(o), partBBullet(o), supplementBullet(o)],
      };
    }
    case "eligibility-gap": {
      const o = out as Extract<typeof out, { baseRateMayApply: boolean }>;
      return {
        headline: eligibilityGapHeadline(o),
        bullets: [eligibilityGapSupplementBullet(o)],
      };
    }
    case "debt-risk": {
      const o = out as Extract<typeof out, { combinedDebtExposure: number }>;
      return {
        headline: debtRiskHeadline(o),
        bullets: debtRiskComponentBullets(o),
      };
    }
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  caveat: {
    fontFamily: fonts.bodyMedium,
    fontSize: 13,
    lineHeight: 19,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
